(() => {
  'use strict';

  const q = (s, c = document) => c?.querySelector?.(s) || null;
  const qa = (s, c = document) => c?.querySelectorAll ? [...c.querySelectorAll(s)] : [];
  const shopRoot = () => window.Shopify?.routes?.root || '/';
  const esc = value => String(value ?? '').replace(/[&<>'"]/g, ch => ({'&':'&amp;','<':'&lt;','>':'&gt;',"'":'&#39;','"':'&quot;'}[ch]));
  const money = (cents, currency = 'EUR') => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency }).format((cents || 0) / 100);
    } catch (e) {
      return `${((cents || 0) / 100).toFixed(2)} ${currency}`;
    }
  };

  const Cart = {
    drawer: null,
    overlay: null,
    init() {
      this.drawer = q('[data-mab-cart-drawer]');
      this.overlay = q('[data-mab-cart-overlay]');
      qa('[data-mab-cart-open]').forEach(btn => {
        if (btn.dataset.mabBoundCartOpen) return;
        btn.dataset.mabBoundCartOpen = '1';
        btn.addEventListener('click', e => { e.preventDefault(); this.open(); });
      });
      const close = q('[data-mab-cart-close]');
      if (close && !close.dataset.mabBoundCartClose) {
        close.dataset.mabBoundCartClose = '1';
        close.addEventListener('click', () => this.close());
      }
      if (this.overlay && !this.overlay.dataset.mabBoundCartOverlay) {
        this.overlay.dataset.mabBoundCartOverlay = '1';
        this.overlay.addEventListener('click', () => this.close());
      }
      if (!document.documentElement.dataset.mabBoundCartKey) {
        document.documentElement.dataset.mabBoundCartKey = '1';
        document.addEventListener('keydown', e => { if (e.key === 'Escape') this.close(); });
      }
      this.refresh(false);
    },
    async add(id, quantity = 1, properties) {
      if (!id) return;
      try {
        const body = { items: [{ id: Number(id), quantity: Number(quantity) || 1 }] };
        if (properties) body.items[0].properties = properties;
        const response = await fetch(shopRoot() + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body)
        });
        if (!response.ok) {
          const error = await response.json().catch(() => ({}));
          throw new Error(error.description || error.message || 'Unable to add item');
        }
        await this.refresh(true);
      } catch (err) {
        console.error('[MAB cart]', err);
        window.alert(err.message || 'Unable to add this item.');
      }
    },
    async change(key, quantity) {
      try {
        const response = await fetch(shopRoot() + 'cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity })
        });
        if (!response.ok) throw new Error('Unable to update cart');
        await this.refresh(false);
      } catch (err) {
        console.error('[MAB cart]', err);
      }
    },
    async refresh(openAfter = false) {
      if (!this.drawer) return;
      try {
        const response = await fetch(shopRoot() + 'cart.js', { headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error('Unable to load cart');
        const cart = await response.json();
        qa('[data-mab-cart-count]').forEach(el => {
          el.textContent = cart.item_count;
          el.hidden = cart.item_count === 0;
        });
        const subtotal = q('[data-mab-cart-subtotal]');
        if (subtotal) subtotal.textContent = money(cart.total_price, cart.currency);
        const items = q('[data-mab-cart-items]');
        if (items) {
          if (!cart.items.length) {
            items.innerHTML = `<p class="mab-cart-empty">${esc(this.drawer?.dataset.emptyLabel || 'Your cart is empty.')}</p>`;
          } else {
            items.innerHTML = cart.items.map(item => {
              const title = esc(item.product_title);
              const variant = item.variant_title && item.variant_title !== 'Default Title' ? `<p class="mab-cart-line__meta">${esc(item.variant_title)}</p>` : '';
              const image = item.image ? `<img src="${esc(item.image)}" alt="${title}" loading="lazy">` : '';
              return `<div class="mab-cart-line"><a class="mab-cart-line__image" href="${esc(item.url)}">${image}</a><div><a class="mab-cart-line__title" href="${esc(item.url)}">${title}</a>${variant}<div class="mab-cart-line__price">${item.quantity} × ${money(item.final_price, cart.currency)}</div></div><button class="mab-cart-line__remove" type="button" aria-label="Remove ${title}" data-mab-remove="${esc(item.key)}">×</button></div>`;
            }).join('');
            qa('[data-mab-remove]', items).forEach(btn => btn.addEventListener('click', () => this.change(btn.dataset.mabRemove, 0)));
          }
        }
        const progress = q('.mab-cart-progress');
        if (progress) {
          const threshold = Number(progress.dataset.threshold || 0);
          const fill = q('[data-mab-progress-fill]', progress);
          const copy = q('[data-mab-shipping-copy]', progress);
          if (threshold > 0) {
            const pct = Math.min(100, (cart.total_price / threshold) * 100);
            if (fill) fill.style.width = `${pct}%`;
            if (copy) { const unlocked = this.drawer?.dataset.freeUnlocked || 'You unlocked free shipping.'; const away = this.drawer?.dataset.freeAway || 'away from free shipping.'; copy.textContent = cart.total_price >= threshold ? unlocked : `${money(threshold - cart.total_price, cart.currency)} ${away}`; }
          }
        }
        if (openAfter) this.open();
      } catch (err) {
        console.error('[MAB cart]', err);
      }
    },
    open() {
      if (!this.drawer) return;
      this.drawer.classList.add('mab-open');
      this.overlay?.classList.add('mab-open');
      this.drawer.setAttribute('aria-hidden', 'false');
      document.body.classList.add('mab-cart-open');
      q('[data-mab-cart-close]', this.drawer)?.focus();
    },
    close() {
      this.drawer?.classList.remove('mab-open');
      this.overlay?.classList.remove('mab-open');
      this.drawer?.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('mab-cart-open');
    }
  };
  window.MABCart = Cart;

  function initHeader() {
    const header = q('[data-mab-header]');
    if (!header) return;
    const toggle = q('[data-mab-menu-toggle]');
    const menu = q('[data-mab-mobile-menu]');
    if (!header.dataset.mabBoundScroll) {
      header.dataset.mabBoundScroll = '1';
      const setHeader = () => header.classList.toggle('mab-scrolled', window.scrollY > 20);
      setHeader();
      window.addEventListener('scroll', setHeader, { passive: true });
    }
    if (toggle && !toggle.dataset.mabBoundMenu) {
      toggle.dataset.mabBoundMenu = '1';
      toggle.addEventListener('click', () => {
        const open = menu?.classList.toggle('mab-open');
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        menu?.setAttribute('aria-hidden', open ? 'false' : 'true');
        document.body.classList.toggle('mab-menu-open', !!open);
      });
      qa('a', menu).forEach(a => a.addEventListener('click', () => {
        menu?.classList.remove('mab-open');
        toggle.setAttribute('aria-expanded', 'false');
        document.body.classList.remove('mab-menu-open');
      }));
    }
  }

  function initReveal(scope = document) {
    const els = qa('.mab-reveal', scope).filter(el => !el.dataset.mabRevealBound);
    if (!els.length) return;
    els.forEach(el => { el.dataset.mabRevealBound = '1'; });
    if (!('IntersectionObserver' in window)) {
      els.forEach(el => el.classList.add('mab-in'));
      return;
    }
    const observer = new IntersectionObserver(entries => entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('mab-in');
        observer.unobserve(entry.target);
      }
    }), { threshold: 0.08, rootMargin: '0px 0px -30px' });
    els.forEach(el => observer.observe(el));
  }

  function initCompare(scope = document) {
    qa('[data-mab-compare]', scope).forEach(wrap => {
      if (wrap.dataset.mabCompareBound) return;
      wrap.dataset.mabCompareBound = '1';
      const input = q('input[type="range"]', wrap);
      const top = q('[data-mab-compare-top]', wrap);
      const handle = q('[data-mab-compare-handle]', wrap);
      const image = q('[data-mab-before-image]', wrap);
      const update = () => {
        const value = Number(input?.value || 50);
        if (top) top.style.width = `${value}%`;
        if (handle) handle.style.left = `${value}%`;
        if (image) image.style.width = `${wrap.clientWidth}px`;
      };
      input?.addEventListener('input', update);
      window.addEventListener('resize', update, { passive: true });
      requestAnimationFrame(update);
    });
  }

  function initQuickAdds(scope = document) {
    qa('[data-mab-add-variant]', scope).forEach(btn => {
      if (btn.dataset.mabAddBound) return;
      btn.dataset.mabAddBound = '1';
      btn.addEventListener('click', e => {
        e.preventDefault();
        if (btn.dataset.mabAddVariant) Cart.add(btn.dataset.mabAddVariant, 1);
      });
    });
  }

  function initFaq(scope = document) {
    qa('[data-mab-faq-list]', scope).forEach(list => {
      qa('details', list).forEach(detail => {
        if (detail.dataset.mabFaqBound) return;
        detail.dataset.mabFaqBound = '1';
        detail.addEventListener('toggle', () => {
          if (detail.open) qa('details', list).forEach(other => { if (other !== detail) other.open = false; });
        });
      });
    });
  }

  function initHome() {
    if (!q('.mab-home')) return;

    const results = q('#mab-results');
    if (results) {
      const after = q('[data-mab-results-after]', results);
      const afterLabel = q('[data-mab-results-after-label]', results);
      qa('[data-mab-result-tab]', results).forEach(tab => {
        if (tab.dataset.mabResultBound) return;
        tab.dataset.mabResultBound = '1';
        tab.addEventListener('click', () => {
          qa('[data-mab-result-tab]', results).forEach(item => item.classList.remove('mab-active'));
          tab.classList.add('mab-active');
          if (after && tab.dataset.image) {
            after.style.opacity = '0';
            const next = tab.dataset.image;
            const reveal = () => { after.style.opacity = '1'; after.removeEventListener('load', reveal); };
            after.addEventListener('load', reveal);
            after.src = next;
            if (after.complete) reveal();
          }
          if (afterLabel) afterLabel.textContent = `After / ${tab.dataset.label || ''}`;
        });
      });
    }

    const ugcRail = q('[data-mab-ugc-rail]');
    const ugcPrev = q('[data-mab-ugc-prev]');
    const ugcNext = q('[data-mab-ugc-next]');
    if (ugcPrev && !ugcPrev.dataset.mabRailBound) {
      ugcPrev.dataset.mabRailBound = '1';
      ugcPrev.addEventListener('click', () => ugcRail?.scrollBy({ left: -320, behavior: 'smooth' }));
    }
    if (ugcNext && !ugcNext.dataset.mabRailBound) {
      ugcNext.dataset.mabRailBound = '1';
      ugcNext.addEventListener('click', () => ugcRail?.scrollBy({ left: 320, behavior: 'smooth' }));
    }
    qa('[data-mab-play]').forEach(btn => {
      if (btn.dataset.mabVideoPlayBound) return;
      btn.dataset.mabVideoPlayBound = '1';
      btn.addEventListener('click', () => {
        const video = q('video', btn.closest('.mab-ugc-video-wrap'));
        if (!video) return;
        if (video.paused) { video.play().catch(() => {}); btn.textContent = 'Ⅱ'; }
        else { video.pause(); btn.textContent = '▶'; }
      });
    });
    qa('[data-mab-mute]').forEach(btn => {
      if (btn.dataset.mabVideoMuteBound) return;
      btn.dataset.mabVideoMuteBound = '1';
      btn.addEventListener('click', () => {
        const video = q('video', btn.closest('.mab-ugc-video-wrap'));
        if (!video) return;
        video.muted = !video.muted;
        btn.textContent = video.muted ? '⌁' : '◖';
      });
    });

    const mood = q('.mab-mood');
    if (mood) {
      const image = q('[data-mab-mood-image]', mood);
      const caption = q('[data-mab-mood-caption]', mood);
      const product = q('[data-mab-mood-product]', mood);
      const desc = q('[data-mab-mood-desc]', mood);
      const add = q('[data-mab-mood-add]', mood);
      qa('[data-mab-mood-option]', mood).forEach(opt => {
        if (opt.dataset.mabMoodBound) return;
        opt.dataset.mabMoodBound = '1';
        opt.addEventListener('click', () => {
          qa('[data-mab-mood-option]', mood).forEach(item => item.classList.remove('mab-active'));
          opt.classList.add('mab-active');
          if (image && opt.dataset.image) image.src = opt.dataset.image;
          if (caption) caption.textContent = opt.dataset.caption || '';
          if (product) product.textContent = opt.dataset.product || '';
          if (desc) desc.textContent = opt.dataset.desc || '';
          if (add) add.dataset.mabAddVariant = opt.dataset.variant || '';
        });
      });
    }

    const how = q('#mab-how');
    if (how && !how.dataset.mabHowBound) {
      how.dataset.mabHowBound = '1';
      const steps = qa('[data-mab-how-step]', how);
      const dots = qa('[data-mab-how-dot]', how);
      if (steps.length && 'IntersectionObserver' in window) {
        const observer = new IntersectionObserver(entries => entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          const index = Number(entry.target.dataset.mabHowStep);
          steps.forEach(step => step.classList.toggle('mab-active', step === entry.target));
          dots.forEach((dot, i) => dot.classList.toggle('mab-active', i === index));
        }), { threshold: 0.55 });
        steps.forEach(step => observer.observe(step));
      }
    }

    const mobileBar = q('[data-mab-mobile-buy]');
    const styles = q('#mab-styles');
    if (mobileBar && styles && !mobileBar.dataset.mabMobileBarBound) {
      mobileBar.dataset.mabMobileBarBound = '1';
      const update = () => {
        const show = window.scrollY > styles.offsetTop + 200 && window.scrollY < document.documentElement.scrollHeight - window.innerHeight - 500;
        mobileBar.classList.toggle('mab-show', show);
      };
      window.addEventListener('scroll', update, { passive: true });
      update();
    }
  }

  function initPdp() {
    const details = q('#mab-pdp-details');
    if (!details) return;

    const mainImage = q('[data-mab-main-image]', details) || q('#MabMainProductImage', details);
    qa('[data-mab-thumb]', details).forEach(btn => {
      if (btn.dataset.mabThumbBound) return;
      btn.dataset.mabThumbBound = '1';
      btn.addEventListener('click', () => {
        qa('[data-mab-thumb]', details).forEach(item => item.classList.remove('mab-active'));
        btn.classList.add('mab-active');
        if (!mainImage || !btn.dataset.src) return;
        mainImage.style.opacity = '0';
        window.setTimeout(() => {
          const reveal = () => { mainImage.style.opacity = '1'; mainImage.removeEventListener('load', reveal); };
          mainImage.addEventListener('load', reveal);
          mainImage.removeAttribute('srcset');
          mainImage.src = btn.dataset.src;
          if (mainImage.complete) reveal();
        }, 80);
      });
    });


    const updatePdpResults = (key, displayName = key) => {
      const selectorName = q('[data-mab-selected-variant-name]', details);
      const selectorDescription = q('[data-mab-selected-variant-description]', details);
      if (selectorName && displayName) selectorName.textContent = String(displayName).trim();
      const results = q('#mab-pdp-results');
      if (!results || !key) return;
      const normalized = String(key).toLowerCase();
      const profiles = qa('[data-mab-result-profile]', results);
      const profile = profiles.find(item => {
        const aliases = (item.dataset.match || '').toLowerCase().split(',').map(v => v.trim()).filter(Boolean);
        return aliases.some(alias => normalized.includes(alias));
      });
      if (!profile) return;

      const before = q('[data-mab-before-image]', results);
      const after = q('[data-mab-after-image]', results);
      const afterLabel = q('[data-mab-results-after-label]', results);
      const heading = q('[data-mab-results-heading]', results);
      const text = q('[data-mab-results-text]', results);
      const values = qa('[data-mab-result-value]', results);
      const beforeSrc = profile.dataset.beforeSrc || '';
      const afterSrc = profile.dataset.afterSrc || '';
      if (before && beforeSrc) { before.removeAttribute('srcset'); before.src = beforeSrc; }
      if (after && afterSrc) { after.removeAttribute('srcset'); after.src = afterSrc; }
      const labelNode = q('[data-profile-after-label]', profile);
      const headingNode = q('[data-profile-heading]', profile);
      const textNode = q('[data-profile-text]', profile);
      const finishNode = q('[data-profile-finish]', profile);
      const bestNode = q('[data-profile-best]', profile);
      const effectNode = q('[data-profile-effect]', profile);
      if (afterLabel && labelNode) afterLabel.textContent = labelNode.textContent.trim();
      if (heading && headingNode) heading.innerHTML = headingNode.innerHTML;
      if (text && textNode) text.textContent = textNode.textContent.trim();
      if (selectorDescription && textNode) selectorDescription.textContent = textNode.textContent.trim();
      const nextValues = [finishNode, bestNode, effectNode];
      values.forEach((node, index) => { if (nextValues[index]) node.textContent = nextValues[index].textContent.trim(); });
    };

    const form = q('#MabProductForm', details);
    const qty = q('[data-mab-qty]', details);
    const count = q('[data-mab-qty-count]', details);
    const variant = q('[data-mab-variant-select]', details);
    const add = q('[data-mab-main-add]', details);
    const sticky = q('[data-mab-mobile-add]', details);
    const minus = q('[data-mab-qty-minus]', details);
    const plus = q('[data-mab-qty-plus]', details);

    const initialOption = variant?.selectedOptions?.[0];
    const initialVariantName = initialOption?.dataset.variantTitle || details.dataset.productTitle || '';
    updatePdpResults(initialVariantName, initialVariantName);

    if (minus && !minus.dataset.mabQtyBound) {
      minus.dataset.mabQtyBound = '1';
      minus.addEventListener('click', () => {
        if (!qty) return;
        qty.value = Math.max(1, Number(qty.value || 1) - 1);
        if (count) count.textContent = qty.value;
      });
    }
    if (plus && !plus.dataset.mabQtyBound) {
      plus.dataset.mabQtyBound = '1';
      plus.addEventListener('click', () => {
        if (!qty) return;
        qty.value = Number(qty.value || 1) + 1;
        if (count) count.textContent = qty.value;
      });
    }
    if (variant && !variant.dataset.mabVariantBound) {
      variant.dataset.mabVariantBound = '1';
      variant.addEventListener('change', () => {
        const opt = variant.selectedOptions[0];
        if (!opt) return;
        updatePdpResults(opt.dataset.variantTitle || opt.textContent || '', opt.dataset.variantTitle || opt.textContent || '');
        const available = opt.dataset.available !== 'false';
        const price = q('[data-mab-price]', details);
        const comparePrice = q('[data-mab-compare-price]', details);
        const saleLabel = q('[data-mab-sale-label]', details);
        const stickyPrice = q('[data-mab-sticky-price]', details);
        if (price && opt.dataset.price) price.textContent = opt.dataset.price;
        if (stickyPrice && opt.dataset.price) stickyPrice.textContent = opt.dataset.price;
        if (comparePrice) {
          comparePrice.textContent = opt.dataset.comparePrice || '';
          comparePrice.hidden = !opt.dataset.comparePrice;
        }
        if (saleLabel) saleLabel.hidden = opt.dataset.onSale !== 'true';
        if (add) {
          add.disabled = !available;
          if (opt.dataset.price) { const addLabel = details.dataset.addLabel || 'Add to cart'; const soldOutLabel = details.dataset.soldOutLabel || 'Sold out'; add.textContent = available ? `${addLabel} — ${opt.dataset.price}` : soldOutLabel; }
        }
        if (sticky) sticky.disabled = !available;
        if (opt.dataset.image && mainImage) {
          mainImage.removeAttribute('srcset');
          mainImage.src = opt.dataset.image;
        }
      });
    }
    const ajaxSubmit = async event => {
      event?.preventDefault?.();
      const id = variant?.value || form?.querySelector('[name="id"]')?.value;
      if (id) await Cart.add(id, Number(qty?.value || 1));
    };
    if (form && !form.dataset.mabFormBound) {
      form.dataset.mabFormBound = '1';
      form.addEventListener('submit', event => {
        // Preserve Shopify's dynamic checkout behavior; AJAX only the MAB Add-to-cart submit.
        const submitter = event.submitter;
        if (!submitter || submitter.matches('[data-mab-main-add]')) ajaxSubmit(event);
      });
    }
    if (sticky && !sticky.dataset.mabStickyBound) {
      sticky.dataset.mabStickyBound = '1';
      sticky.addEventListener('click', ajaxSubmit);
    }

    const pdpRail = q('[data-mab-pdp-ugc-rail]');
    const pdpPrev = q('[data-mab-pdp-ugc-prev]');
    const pdpNext = q('[data-mab-pdp-ugc-next]');
    if (pdpPrev && !pdpPrev.dataset.mabRailBound) {
      pdpPrev.dataset.mabRailBound = '1';
      pdpPrev.addEventListener('click', () => pdpRail?.scrollBy({ left: -320, behavior: 'smooth' }));
    }
    if (pdpNext && !pdpNext.dataset.mabRailBound) {
      pdpNext.dataset.mabRailBound = '1';
      pdpNext.addEventListener('click', () => pdpRail?.scrollBy({ left: 320, behavior: 'smooth' }));
    }
    qa('.mab-pdp video').forEach(video => {
      if (video.dataset.mabAutoVideoBound || !('IntersectionObserver' in window)) return;
      video.dataset.mabAutoVideoBound = '1';
      const observer = new IntersectionObserver(entries => entries.forEach(entry => {
        if (entry.isIntersecting) video.play().catch(() => {});
        else video.pause();
      }), { threshold: 0.6 });
      observer.observe(video);
    });
  }

  function initAll(scope = document) {
    initHeader();
    Cart.init();
    initReveal(scope);
    initCompare(scope);
    initQuickAdds(scope);
    initFaq(scope);
    initHome();
    initPdp();
  }

  document.addEventListener('DOMContentLoaded', () => initAll(document));
  document.addEventListener('shopify:section:load', event => initAll(event.target));
})();
